import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Box, CircularProgress, Stack, Typography, Alert, Checkbox, FormControlLabel, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';

// =============================================================================
// TAP PAYMENT CONFIGURE 
// =============================================================================
const TAP_CONFIG = {
    publicKey: process.env.REACT_APP_TAP_PUBLIC_KEY || 'pk_test_zpZr9J7P0dCRL8FsUeWh52GO',
    sdkUrl: 'https://tap-sdks.b-cdn.net/card/1.0.2/index.js',
    // merchantId: process.env.REACT_APP_TAP_MERCHANT_ID || 'merchant_id'
};

// =============================================================================
// TAP CARD INPUT
// =============================================================================
const TapCardInput = forwardRef(({
    amount,
    currency = 'SAR',
    customerEmail,
    customerPhone,
    customerName,
    onReady,
    onValidInput,
    onInvalidInput,
    onError,
    onSuccess,
    onFocus,
    onBinIdentification,
    showLoader = true,
    showError = true,
    containerStyle,
    loaderComponent,
    // Save Card Props
    showSaveCard = true,
    saveCardDefaultChecked = true,
    onSaveCardChange,
}, ref) => {
    const { t, i18n } = useTranslation();
    const cardContainerRef = useRef(null);
    const unmountRef = useRef(null);
    const initializedRef = useRef(false);
    const containerIdRef = useRef(`tap-card-container-${Math.random().toString(36).substr(2, 9)}`);

    const [sdkReady, setSdkReady] = useState(false);
    const [sdkLoading, setSdkLoading] = useState(true);
    const [cardValid, setCardValid] = useState(false);
    const [error, setError] = useState(null);
    const [saveCard, setSaveCard] = useState(saveCardDefaultChecked);

    // =============================================================================
    // EXPOSE METHODS VIA REF
    // =============================================================================
    useImperativeHandle(ref, () => ({
        tokenize: async () => {
            if (!window.CardSDK?.tokenize) {
                throw new Error(t('Payment SDK not ready'));
            }
            return window.CardSDK.tokenize();
        },
        reset: () => {
            if (window.CardSDK?.resetCardInputs) {
                window.CardSDK.resetCardInputs();
            }
            setCardValid(false);
            setError(null);
        },
        isReady: () => sdkReady,
        isValid: () => cardValid,
        getError: () => error,
        getSaveCard: () => saveCard,
    }));

    // =============================================================================
    // LOAD TAP SDK SCRIPT
    // =============================================================================
    const loadTapScript = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (window.CardSDK) {
                resolve(window.CardSDK);
                return;
            }

            const existingScript = document.querySelector(`script[src="${TAP_CONFIG.sdkUrl}"]`);
            if (existingScript) {
                existingScript.addEventListener('load', () => resolve(window.CardSDK));
                existingScript.addEventListener('error', reject);
                return;
            }

            const script = document.createElement('script');
            script.src = TAP_CONFIG.sdkUrl;
            script.async = true;
            script.onload = () => resolve(window.CardSDK);
            script.onerror = () => reject(new Error('Failed to load Tap SDK'));
            document.head.appendChild(script);
        });
    }, []);

    // =============================================================================
    // INITIALIZE TAP CARD SDK
    // =============================================================================
    const initializeTapCard = useCallback(async () => {
        // Skip if already initialized
        if (initializedRef.current) {
            return;
        }

        try {
            setSdkLoading(true);
            setError(null);

            const CardSDK = await loadTapScript();

            if (!CardSDK || !CardSDK.renderTapCard) {
                throw new Error('Tap SDK not loaded properly');
            }

            const { renderTapCard, Theme, Currencies, Direction, Edges, Locale } = CardSDK;

            await new Promise(resolve => setTimeout(resolve, 100));

            const containerId = containerIdRef.current;
            const containerElement = document.getElementById(containerId);

            // Check if container exists and is visible
            if (!containerElement) {
                throw new Error('Card container not found');
            }

            // Check if container is visible (not display: none)
            if (containerElement.offsetParent === null && getComputedStyle(containerElement).display === 'none') {
                // Container is hidden, skip initialization for now
                setSdkLoading(false);
                return;
            }

            if (unmountRef.current) {
                try {
                    unmountRef.current();
                } catch (e) {
                    // console.warn('Error unmounting previous card instance:', e);
                    console.log('Error unmounting previous card instance:', e);
                }
            }

            const { unmount } = renderTapCard(containerId, {
                publicKey: TAP_CONFIG.publicKey,
                merchant: {
                    id: TAP_CONFIG.merchantId
                },
                transaction: {
                    amount: amount,
                    currency: Currencies[currency] || Currencies.SAR
                },
                customer: {
                    name: customerName ? [
                        {
                            lang: Locale.EN,
                            first: customerName.split(' ')[0] || '',
                            last: customerName.split(' ').slice(1).join(' ') || '',
                            middle: ''
                        }
                    ] : [],
                    nameOnCard: customerName || '',
                    editable: true,
                    contact: {
                        email: customerEmail || '',
                        phone: customerPhone ? {
                            countryCode: customerPhone.countryCode || '966',
                            number: customerPhone.number || ''
                        } : {}
                    }
                },
                acceptance: {
                    supportedBrands: ['VISA', 'MASTERCARD', 'MADA', 'AMERICAN_EXPRESS'],
                    supportedCards: 'ALL'
                },
                fields: {
                    cardHolder: true
                },
                addons: {
                    displayPaymentBrands: true,
                    loader: true,
                    saveCard: false
                },
                interface: {
                    locale: i18n.language === 'ar' ? Locale.AR : Locale.EN,
                    theme: Theme.LIGHT,
                    edges: Edges.CURVED,
                    direction: i18n.language === 'ar' ? Direction.RTL : Direction.LTR
                },
                onReady: () => {
                    console.log('Tap Card SDK Ready');
                    setSdkReady(true);
                    setSdkLoading(false);
                    onReady?.();
                },
                onFocus: () => {
                    console.log('Card input focused');
                    onFocus?.();
                },
                onBinIdentification: (data) => {
                    console.log('Card BIN identified:', data);
                    onBinIdentification?.(data);
                },
                onValidInput: (data) => {
                    console.log('Valid input:', data);
                    setCardValid(true);
                    setError(null);
                    onValidInput?.(data);
                },
                onInvalidInput: (data) => {
                    console.log('Invalid input:', data);
                    setCardValid(false);
                    onInvalidInput?.(data);
                },
                onError: (data) => {
                    console.log('Card SDK Error:', data);
                    const errorMessage = data?.error?.message || t('Card input error');
                    setError(errorMessage);
                    setCardValid(false);
                    onError?.(data);
                },
                onSuccess: (data) => {
                    console.log('Token created successfully:', data);
                    onSuccess?.(data);
                }
            });

            unmountRef.current = unmount;
            initializedRef.current = true;

        } catch (err) {
            console.log('Error initializing Tap Card:', err);
            const errorMessage = err.message || t('Failed to initialize payment form');
            setError(errorMessage);
            setSdkLoading(false);
            onError?.({ error: { message: errorMessage } });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadTapScript, t]);

    // =============================================================================
    // INITIALIZATION EFFECT - Run only once on mount
    // =============================================================================
    useEffect(() => {
        const timer = setTimeout(() => {
            initializeTapCard();
        }, 200);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // =============================================================================
    // CLEANUP ON UNMOUNT
    // =============================================================================
    useEffect(() => {
        return () => {
            if (unmountRef.current) {
                try {
                    unmountRef.current();
                } catch (e) {
                    console.log('Error unmounting card on cleanup:', e);
                }
            }
            initializedRef.current = false;
        };
    }, []);

    // =============================================================================
    // RENDER
    // =============================================================================
    const defaultLoader = (
        <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: 'background.paper',
                zIndex: 1,
                transition: "all 1s ease-in-out",
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    marginLeft: "auto",
                    boxSizing: "border-box",
                    marginRight: "auto",
                    display: "flex",
                    maxWidth: 500,
                    direction: "ltr",
                    transition: "all 1s ease-in-out",
                }}>
                <Box display="flex" flexDirection="column" gap={1} width="100%">
                    <Skeleton variant="rounded" width="100%" height={45} />
                    <Stack sx={{ mr: 2 }} style={{ direction: "ltr" }} direction="row" alignItems="center">
                        <Skeleton sx={{ ml: 1.5 }} variant="rounded" width={54} height={15} />
                        <Skeleton sx={{ ml: 1.5 }} variant="rounded" width={27} height={15} />
                        <Skeleton sx={{ ml: 1.5 }} variant="rounded" width={27} height={15} />
                        <Skeleton sx={{ ml: 1.5 }} variant="rounded" width={27} height={15} />
                    </Stack>
                </Box>
            </Box>
        </Stack>
    );

    return (
        <Box sx={{ position: 'relative', ...containerStyle }}>
            <Box sx={{ minHeight: 120, position: 'relative' }}>
                {(showLoader && sdkLoading && sdkLoading) && (loaderComponent || defaultLoader)}

                <Box
                    id={containerIdRef.current}
                    ref={cardContainerRef}
                    sx={{
                        '& iframe': {
                            width: '100% !important'
                        }
                    }}
                />
            </Box>

            {showError && error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}

            {showSaveCard && (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={saveCard}
                            onChange={(e) => {
                                setSaveCard(e.target.checked);
                                onSaveCardChange?.(e.target.checked);
                            }}
                            color="primary"
                        />
                    }
                    label={t('Save card for future payments')}
                />
            )}
        </Box>
    );
});

TapCardInput.displayName = 'TapCardInput';

export default TapCardInput;

// =============================================================================
// USAGE EXAMPLE:
// =============================================================================
//
// const cardRef = useRef(null);
//
// const handlePayClick = async () => {
//     try {
//         await cardRef.current.tokenize();
//     } catch (err) {
//         console.error('Tokenization error:', err);
//     }
// };
//
// <TapCardInput
//     ref={cardRef}
//     amount={100}
//     currency="SAR"
//     customerEmail="test@example.com"
//     customerName="John Doe"
//     onSuccess={(tokenData) => {
//         // Send tokenData.id to your backend
//     }}
//     onError={(error) => {
//         console.error('Error:', error);
//     }}
// />
// <Button onClick={handlePayClick}>Pay</Button>
